// The brachistochrone race beside the title: three balls start together at A and roll without
// friction to B along a straight line, a circular arc and a cycloid. Real physics (v = sqrt(2gh),
// time integrated along each path); the cycloid always wins. Each ball lights its path behind it;
// the cycloid's arrival sends a ring out from B. The circle that generates the cycloid rolls along
// the top line in step with its ball (its angle grows evenly with time).
//
// Copied from the Cycloid portal (portal/static/race.js). BCL changes: runs on every
// canvas[data-bl-race] (theme editor safe), takes its colours from data attributes so it
// works on light and dark grounds (data-glow="0" drops the glow on light grounds), fades in
// gently the first time it scrolls into view, and pauses (then resumes) while off-screen.
// data-mode="scroll": no clock; the race is scrubbed by scroll position (Apple-style). Inside a
// .bl-scrub wrapper the progress runs from the wrapper's top to its bottom while the canvas is
// pinned; elsewhere it runs while the canvas crosses the viewport. Phones (under 750px) always
// play on their own.
// data-pixel="4": the lit trails and balls get an E Ink style dot mosaic around them, and the
// arrival rings are drawn in dots. Soft bands are painted into a small grid canvas (one pixel per
// cell), then every cell is dithered against a fixed random threshold into a square dot, so the
// dots thin out away from the line, scatter like the LogCase mosaic and never flicker.
(() => {
function race(canvas) {
  if (canvas.dataset.blRaceReady) return;
  canvas.dataset.blRaceReady = "1";
  const ctx = canvas.getContext("2d");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const d = canvas.dataset;
  const COLORS = { cycloid: d.cycloid || "#e6e4a7", arc: d.arc || "#4272a2", line: d.line || "#828a84" };
  const MARK = d.mark || "#f8f8f8", GUIDE = d.guide || "rgba(248,248,248,.16)", WHEEL = d.wheel || "rgba(230,228,167,.3)";
  const GLOW = d.glow !== "0";
  // phones skip the scrub (the CSS unpins it there) and loop on their own instead
  const SCROLL = d.mode === "scroll" && !window.matchMedia("(max-width: 749px)").matches;
  const track = SCROLL ? canvas.closest(".bl-scrub") : null;
  const CELL = parseFloat(d.pixel) || 0;
  const grid = CELL ? document.createElement("canvas") : null;
  const gctx = grid && grid.getContext("2d", { willReadFrequently: true });
  let noise = null; // a fixed random threshold per cell, so the dots stay put from frame to frame
  let target = 0, prog = 0;
  const INTRO = 1.6; // seconds: rails fade in, then the balls appear and the first race starts
  let visible = true, running = false, shown = null, pausedAt = null;
  const clamp01 = (v) => Math.min(Math.max(v, 0), 1);
  const easeOut = (v) => 1 - (1 - v) ** 3;
  const HOLD = 1.6, FADE = 0.7; // seconds after the last ball arrives: hold, then fade out
  let W = 0, H = 0, A, B, paths, cyc, start = 0;

  // cycloid through A and B: x = r(t - sin t), y = r(1 - cos t), y downwards
  function cycloid(dx, dy) {
    let lo = 1e-6, hi = 2 * Math.PI - 1e-6;
    const want = dx / dy;
    for (let i = 0; i < 80; i++) {
      const m = (lo + hi) / 2;
      ((m - Math.sin(m)) / (1 - Math.cos(m)) < want ? (lo = m) : (hi = m));
    }
    const te = (lo + hi) / 2;
    return { te, r: dy / (1 - Math.cos(te)) };
  }

  // pinned layout: tell the CSS how tall the heading block is so the canvas takes the rest
  const sticky = track && track.querySelector(".bl-scrub__sticky");
  const head = sticky && sticky.querySelector(".bl-brach__head");
  function sizeHead() {
    if (head) sticky.style.setProperty("--bl-brach-head", head.offsetHeight + "px");
  }

  function build() {
    sizeHead();
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = rect.width; H = rect.height;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (grid) {
      grid.width = Math.ceil(W / CELL); grid.height = Math.ceil(H / CELL);
      noise = new Float32Array(grid.width * grid.height);
      for (let i = 0; i < noise.length; i++) {
        const h = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
        noise[i] = h - Math.floor(h);
      }
      gctx.setTransform(1 / CELL, 0, 0, 1 / CELL, 0, 0);
    }
    // tall or square canvases (phones): run corner to corner for a steep, dramatic drop and let
    // the generating circle overflow the edge rather than shrinking the race
    const tall = H > W * 0.85;
    if (tall) {
      // keep the slope moderate (drop at most ~1.1x the run) so the three paths stay distinct,
      // and centre the race vertically in whatever height is left
      const dx = W * 0.86, dy = Math.min(H * 0.84, dx * 1.1);
      A = { x: W * 0.07, y: (H - dy) / 2 };
      B = { x: W * 0.93, y: (H + dy) / 2 };
    } else {
      A = { x: W * 0.06, y: H * 0.12 };
      B = { x: W * 0.94, y: H * 0.62 };
    }
    cyc = cycloid(B.x - A.x, B.y - A.y);
    // bring B in until the generating circle, at the finish, stays inside the canvas, and so does
    // the cycloid's lowest point (A.y + 2r, reached when it dips below B on wide canvases)
    while (!tall && (A.x + cyc.r * (cyc.te + 1) > W - 4 || (cyc.te > Math.PI && A.y + 2 * cyc.r > H - 8)) && B.x > W * 0.6) {
      B.x -= W * 0.01;
      cyc = cycloid(B.x - A.x, B.y - A.y);
    }
    const dx = B.x - A.x, dy = B.y - A.y;
    const N = 240;
    const line = [], arc = [], cy = [];
    for (let i = 0; i <= N; i++) {
      const f = i / N;
      line.push({ x: A.x + dx * f, y: A.y + dy * f });
      const t = cyc.te * f;
      cy.push({ x: A.x + cyc.r * (t - Math.sin(t)), y: A.y + cyc.r * (1 - Math.cos(t)) });
    }
    // a circular arc from A to B bulging downwards (centre above the chord)
    const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2, len = Math.hypot(dx, dy);
    const nx = dy / len, ny = -dx / len; // unit normal pointing up-right
    const sag = len * 0.1, R = (sag * sag + (len / 2) ** 2) / (2 * sag);
    const cx = mx + nx * (R - sag), cyy = my + ny * (R - sag);
    let a0 = Math.atan2(A.y - cyy, A.x - cx), a1 = Math.atan2(B.y - cyy, B.x - cx);
    if (a1 > a0) a1 -= 2 * Math.PI;
    for (let i = 0; i <= N; i++) {
      const a = a0 + (a1 - a0) * (i / N);
      arc.push({ x: cx + R * Math.cos(a), y: cyy + R * Math.sin(a) });
    }
    paths = { line: timed(line), arc: timed(arc), cycloid: timed(cy) };
    // scale so the cycloid takes 2.4 s
    const k = 2.4 / paths.cycloid.total;
    for (const p of Object.values(paths)) { p.t = p.t.map((v) => v * k); p.total *= k; }
  }

  // time to reach each point, from rest at A: v = sqrt(2 g h), h measured down from A
  function timed(pts) {
    const g = 1, t = [0];
    const v = (p) => Math.sqrt(2 * g * Math.max(p.y - A.y, 0));
    for (let i = 1; i < pts.length; i++) {
      const l = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      const vs = v(pts[i - 1]) + v(pts[i]);
      t.push(t[i - 1] + (vs > 0 ? (2 * l) / vs : Math.sqrt((2 * l) / g)));
    }
    return { pts, t, total: t[t.length - 1] };
  }

  // where a ball is at `time`, and the index of the last path point it has passed
  function at(p, time) {
    const last = p.pts.length - 1;
    if (time >= p.total) return { ...p.pts[last], i: last };
    let lo = 0, hi = last;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; p.t[m] <= time ? (lo = m) : (hi = m); }
    const f = (time - p.t[lo]) / (p.t[hi] - p.t[lo] || 1);
    return {
      x: p.pts[lo].x + (p.pts[hi].x - p.pts[lo].x) * f,
      y: p.pts[lo].y + (p.pts[hi].y - p.pts[lo].y) * f,
      i: lo,
    };
  }

  function stroke(pts, color, width, alpha, glow = 0, c = ctx) {
    if (pts.length < 2) return;
    const ctx = c;
    ctx.save();
    ctx.globalAlpha = alpha; ctx.strokeStyle = color; ctx.lineWidth = width;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (glow && GLOW) { ctx.shadowColor = color; ctx.shadowBlur = glow; }
    ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.stroke(); ctx.restore();
  }

  function dot(p, r, color, alpha, glow = 0, c = ctx) {
    const ctx = c;
    ctx.save();
    ctx.globalAlpha = alpha; ctx.fillStyle = color;
    if (glow && GLOW) { ctx.shadowColor = color; ctx.shadowBlur = glow; }
    ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, 2 * Math.PI); ctx.fill();
    ctx.restore();
  }

  // one square dot per grid cell whose coverage beats its threshold
  function mosaic() {
    const gw = grid.width, gh = grid.height, px = gctx.getImageData(0, 0, gw, gh).data;
    const size = CELL - Math.max(1, CELL * 0.18);
    ctx.save();
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const i = (y * gw + x) * 4, a = px[i + 3] / 255;
        if (a <= noise[y * gw + x]) continue;
        ctx.fillStyle = `rgb(${px[i]},${px[i + 1]},${px[i + 2]})`;
        ctx.fillRect(x * CELL, y * CELL, size, size);
      }
    }
    ctx.restore();
  }

  function frame(now) {
    if (SCROLL) prog += (target - prog) * 0.14;
    const raw = reduce ? 1e9 : SCROLL ? prog * (paths.line.total + 0.8) : (now - start) / 1000;
    const time = Math.max(raw, 0);
    const end = paths.line.total;
    // first appearance: the rails fade in, then the balls; each later race fades its balls in at A
    const age = reduce || SCROLL || shown === null ? 1e9 : (now - shown) / 1000;
    const appear = easeOut(clamp01(age / INTRO));
    const balls = reduce || SCROLL ? 1 : easeOut(clamp01((age - INTRO * 0.55) / 0.6)) * clamp01((raw + 0.5) / 0.5);
    // everything lit fades out together before the next race
    const lit = reduce || SCROLL ? 1 : 1 - Math.min(Math.max((time - end - HOLD) / FADE, 0), 1);
    ctx.clearRect(0, 0, W, H);

    // the top line the generating circle rolls along
    ctx.save();
    ctx.setLineDash([2, 6]); ctx.strokeStyle = GUIDE; ctx.lineWidth = 1; ctx.globalAlpha = appear;
    ctx.beginPath(); ctx.moveTo(A.x - 12, A.y); ctx.lineTo(B.x + 12, A.y); ctx.stroke();
    ctx.restore();

    // the three rails, faint
    stroke(paths.line.pts, COLORS.line, 1, 0.35 * appear);
    stroke(paths.arc.pts, COLORS.arc, 1, 0.45 * appear);
    stroke(paths.cycloid.pts, COLORS.cycloid, 1, 0.4 * appear);

    // generating circle, rolling in step with the cycloid ball
    const f = Math.min(time / paths.cycloid.total, 1), th = cyc.te * f;
    const cx = A.x + cyc.r * th, cy = A.y + cyc.r;
    ctx.save();
    ctx.globalAlpha = 0.6 * lit * balls;
    ctx.strokeStyle = WHEEL; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, cyc.r, 0, 2 * Math.PI); ctx.stroke();
    const rim = { x: cx - cyc.r * Math.sin(th), y: cy - cyc.r * Math.cos(th) };
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(rim.x, rim.y); ctx.stroke();
    ctx.restore();

    // each ball lights its rail behind it; the cycloid brightest, drawn last (on top)
    const g = grid ? gctx : ctx;
    if (grid) gctx.clearRect(0, 0, W, H);
    const lines = [];
    for (const key of ["line", "arc", "cycloid"]) {
      const p = paths[key], pos = at(p, time), win = key === "cycloid";
      const trail = p.pts.slice(0, pos.i + 1).concat(pos);
      const a = (win ? 1 : 0.8) * lit * balls;
      if (grid) {
        // a wide soft band (denser near the ball) that dithers into loose dots along the trail
        stroke(trail, COLORS[key], CELL * 5, a * 0.22, 0, g);
        stroke(trail.slice(-40), COLORS[key], CELL * 3, a * 0.3, 0, g);
        dot(pos, CELL * 4, COLORS[key], lit * balls * 0.4, 0, g);
      }
      lines.push([trail, pos, key, win, a]);
    }

    // the cycloid arrives: two rings spread from B
    const since = time - paths.cycloid.total;
    if (!reduce && since > 0) {
      for (const d of [0, 0.25]) {
        const s = since - d;
        if (s <= 0 || s > 1.4) continue;
        const e = 1 - (1 - s / 1.4) ** 3;
        g.save();
        g.globalAlpha = (1 - s / 1.4) * 0.7; g.strokeStyle = COLORS.cycloid; g.lineWidth = grid ? CELL : 1.2;
        g.beginPath(); g.arc(B.x, B.y, 6 + e * 46, 0, 2 * Math.PI); g.stroke();
        g.restore();
      }
    }
    if (grid && grid.width && grid.height) mosaic();
    // the smooth trails and balls sit on top of the dots
    for (const [trail, pos, key, win, a] of lines) {
      stroke(trail, COLORS[key], win ? 2.2 : 1.6, a, win ? 14 : 0);
      dot(pos, win ? 6 : 5, COLORS[key], lit * balls, win ? 22 : 10);
    }

    // start and finish marks
    dot(A, 2.5, MARK, 0.5 * appear);
    dot(B, 2.5, MARK, 0.5 * appear);

    if (SCROLL) {
      if (!reduce && Math.abs(target - prog) > 0.0005) requestAnimationFrame(frame);
      else { prog = target; running = false; }
      return;
    }
    if (!reduce && time > end + HOLD + FADE + 0.3) start = now + 600;
    if (!reduce && visible) requestAnimationFrame(frame);
    else running = false;
  }

  function run() {
    if (running || !canvas.isConnected) return;
    running = true;
    requestAnimationFrame(frame);
  }

  function show(now) {
    if (shown === null) { shown = now; start = now + INTRO * 1000; }
    else if (pausedAt !== null) { shown += now - pausedAt; start += now - pausedAt; }
    pausedAt = null;
    run();
  }

  // 0 at the start of the scrub, 1 at the end
  function measure() {
    const vh = window.innerHeight;
    if (track) {
      const r = track.getBoundingClientRect();
      const span = r.height - canvas.closest(".bl-scrub__sticky").offsetHeight;
      return clamp01(-r.top / Math.max(span, 1));
    }
    const r = canvas.getBoundingClientRect();
    const centre = r.top + r.height / 2;
    return clamp01((vh * 0.95 - centre) / (vh * 0.55));
  }

  build();
  // Rebuild whenever the canvas box changes size, not only on window resize: the theme sets
  // --header-height and web fonts reflow the heading after load, which resizes the box and
  // would otherwise stretch the bitmap (the rolling circle turned into an ellipse).
  function rebuild() {
    sizeHead();
    const r = canvas.getBoundingClientRect();
    if (Math.abs(r.width - W) < 0.5 && Math.abs(r.height - H) < 0.5) return;
    build();
    if (reduce || SCROLL) { target = measure(); prog = target; }
    frame(performance.now());
  }
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(rebuild);
    ro.observe(canvas);
    if (head) ro.observe(head);
  }
  window.addEventListener("resize", rebuild);
  if (SCROLL) {
    target = prog = measure();
    window.addEventListener("scroll", () => { target = measure(); run(); }, { passive: true });
    frame(performance.now());
    return;
  }
  if ("IntersectionObserver" in window && !reduce) {
    visible = false;
    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) show(performance.now());
      else if (pausedAt === null && shown !== null) pausedAt = performance.now();
    }, { threshold: 0.35 }).observe(canvas);
  } else {
    show(performance.now());
  }
}

const init = (root) => (root || document).querySelectorAll("canvas[data-bl-race]").forEach(race);
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
else init();
document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
